import { useCallback, useEffect, useState } from "react";
import { addDays, addWeeks, format, isSameDay } from "date-fns";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { DAY_LABELS, catStyle } from "@/lib/constants";
import { fmtDuration, toDay, weekDays, weekStartOf } from "@/lib/time";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PeriodCard from "@/components/timesheet/PeriodCard";
import EntryDialog from "@/components/timesheet/EntryDialog";
import TeamReport from "@/components/timesheet/TeamReport";

const iconBtn = "w-10 h-10 rounded-full border border-[#103080]/15 bg-white/70 flex items-center justify-center text-[#103080] hover:bg-[#103080] hover:text-white transition-colors duration-200";

function DayColumn({ d, entries, onAdd, onOpen }) {
  const total = entries.reduce((s, e) => s + e.minutes, 0);
  return (
    <div className={`rounded-2xl border p-3 flex flex-col min-h-[220px] ${isSameDay(d, new Date()) ? "border-[#0090D0] bg-[#0090D0]/[0.04]" : "border-[#103080]/10 bg-white/70"}`} data-testid={`timesheet-day-${toDay(d)}`}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-[#103080]"><span className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70">{DAY_LABELS[(d.getDay() + 6) % 7]}</span> <span className="font-serif-brand text-lg font-semibold">{d.getDate()}</span></p>
        <span className="text-xs font-semibold text-[#0090D0]" data-testid={`timesheet-day-total-${toDay(d)}`}>{total ? fmtDuration(total) : ""}</span>
      </div>
      <ul className="space-y-1.5 flex-1">
        {entries.map((e) => {
          const c = catStyle(e.category);
          return (
            <li key={e.id}>
              <button onClick={() => onOpen(e)} data-testid={`time-entry-${e.id}`} className="w-full text-left rounded-lg border-l-[3px] px-2 py-1.5 hover:shadow-sm transition-shadow duration-200" style={{ background: c.bg, borderColor: c.border, color: c.text }}>
                <p className="text-xs font-semibold truncate">{e.label}</p>
                <p className="text-[10px] opacity-80 truncate">{e.client_name || e.category} · {fmtDuration(e.minutes)}</p>
              </button>
            </li>
          );
        })}
      </ul>
      <button onClick={() => onAdd(d)} data-testid={`timesheet-add-${toDay(d)}`}
        className="mt-2 inline-flex items-center justify-center gap-1 rounded-full border border-dashed border-[#103080]/25 h-8 text-xs font-semibold text-[#103080]/70 hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200"><Plus size={12} /> Log time</button>
    </div>
  );
}

export default function Timesheet() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(weekStartOf(new Date()));
  const [viewUser, setViewUser] = useState("");
  const [team, setTeam] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dialog, setDialog] = useState(null);
  const days = weekDays(weekStart);
  const uid = viewUser || null;

  const load = useCallback(async () => {
    const p = uid ? { user_id: uid } : {};
    try {
      const [e, s, t] = await Promise.all([
        api.get("/timesheet/entries", { params: { ...p, start: toDay(weekStart), end: toDay(addDays(weekStart, 6)) } }),
        api.get("/timesheet/summary", { params: { ...p, on: toDay(new Date()) } }),
        api.get("/tasks", { params: p }),
      ]);
      setEntries(e.data);
      setSummary(s.data);
      setTasks(t.data.filter((x) => x.status === "todo" || x.completed_at));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [weekStart, uid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user.role === "admin") api.get("/team").then((r) => setTeam(r.data.filter((m) => m.active))).catch(() => {});
  }, [user.role]);

  const fill = async () => {
    try {
      const { data } = await api.post("/timesheet/fill", { week_start: toDay(weekStart), user_id: uid });
      toast.success(data.created ? `Added ${data.created} entr${data.created === 1 ? "y" : "ies"} from the planner` : "Nothing new to add from the planner");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    await api.post("/timesheet/submit", null, { params: { on: toDay(new Date()) } }).catch((e) => toast.error(formatApiError(e)));
    toast.success("Timesheet submitted");
    load();
  };

  const weekTotal = entries.reduce((s, e) => s + e.minutes, 0);
  return (
    <div className="space-y-6 fade-up" data-testid="timesheet-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Timesheet</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">Week of <i className="text-[#0090D0]">{format(weekStart, "d MMM")}</i></h1>
          <div className="mt-4 flex items-center gap-2">
            <button className={iconBtn} onClick={() => setWeekStart(addWeeks(weekStart, -1))} aria-label="Previous week" data-testid="timesheet-prev-week"><ChevronLeft size={18} /></button>
            <button className={iconBtn} onClick={() => setWeekStart(addWeeks(weekStart, 1))} aria-label="Next week" data-testid="timesheet-next-week"><ChevronRight size={18} /></button>
            <span className="ml-2 text-sm text-[#103080] font-semibold" data-testid="timesheet-week-total">{fmtDuration(weekTotal)} this week</span>
            {team.length > 0 && (
              <Select value={viewUser || "me"} onValueChange={(v) => setViewUser(v === "me" ? "" : v)}>
                <SelectTrigger className="w-48 h-10 rounded-full bg-white/70 border-[#103080]/20 ml-2" data-testid="timesheet-user-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="me">My timesheet</SelectItem>
                  {team.filter((m) => m.id !== user.id).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <button onClick={fill} data-testid="timesheet-fill-button"
          className="inline-flex items-center gap-2 rounded-full border border-[#103080]/20 bg-white/70 text-[#103080] font-medium px-5 h-11 text-sm hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200">
          <Wand2 size={15} /> Fill from planner
        </button>
      </div>
      <PeriodCard s={summary} canSubmit={!uid} onSubmit={submit} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {days.map((d) => (
          <DayColumn key={toDay(d)} d={d} entries={entries.filter((e) => e.date === toDay(d))}
            onAdd={(x) => setDialog({ date: toDay(x) })} onOpen={setDialog} />
        ))}
      </div>
      {user.role === "admin" && <TeamReport />}
      <EntryDialog entry={dialog} tasks={tasks} userId={uid} onClose={() => setDialog(null)} onSaved={load} />
    </div>
  );
}
